import { useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { authService } from "@/lib/auth/authService";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  logout,
  setCredentials,
  setError,
  setLoading,
  setRestoringSession,
} from "@/store/slices/userSlice";
import { setActiveCompany, setCompanies } from "@/store/slices/companySlice";
import { apolloClient } from "@/lib/apollo/client";
import { hasEventSession, removeSessionCookies } from "@/lib/auth/session";
import { useEventsFeature } from "../events";

/**
 * Full auth hook — requires a router context.
 * Use inside any page or component rendered by <RouterProvider>.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentUser = useAppSelector((s) => s.user.currentUser);
  const isAuthenticated = useAppSelector((s) => s.user.isAuthenticated);
  const accessToken = useAppSelector((s) => s.user.accessToken);
  const isLoading = useAppSelector((s) => s.user.isLoading);
  const error = useAppSelector((s) => s.user.error);
  const companies = useAppSelector((s) => s.company.companies);
  const activeCompany = useAppSelector((s) => s.company.activeCompany);

  const { currentEvent } = useEventsFeature();

  const matchedCompany = companies.find(
    (company) => company?.companyId === activeCompany?.id,
  );

  // 2. Safely extract the role (handles case where no match is found)
  const role: string | undefined = matchedCompany?.role;
  // const role = activeRole ?? null;

  const isAdmin = currentEvent?.canManageRequests === true;
  const isManager =
    (role === "ADMIN" || role === "MANAGER") &&
    currentEvent?.organiser?.company?.name === activeCompany?.name;
  // console.info("role", role, isAdmin);

  // ─── Login ────────────────────────────────────────────────────
  const handleLogin = useCallback(
    async (email: string, password: string) => {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const result = await authService.login(email, password);
      if (!result.ok) {
        dispatch(setError(result.error));
        return false;
      }
      dispatch(setCredentials({ user: result.user })); // tokens: result?.tokens
      dispatch(setActiveCompany(result.company ?? null));
      dispatch(setCompanies(result.companies ?? []));
      return true;
    },
    [dispatch],
  );

  // ─── Logout ───────────────────────────────────────────────────
  const handleLogout = useCallback(() => {
    removeSessionCookies();
    dispatch(logout());
    void apolloClient.clearStore();
    void navigate(ROUTES.LOGIN, { replace: true });
  }, [dispatch, navigate]);

  return {
    currentUser,
    isAuthenticated,
    accessToken,
    isLoading,
    error,
    role,
    isAdmin,
    isManager,
    handleLogin,
    handleLogout,
  };
}

/**
 * Standalone session-restore function — no router context required.
 * Called once in App.tsx before <RouterProvider> mounts.
 * Only dispatches to Redux; navigation is handled by AuthGuard/GuestGuard.
 */
export function useRestoreSession() {
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    if (!hasEventSession()) {
      dispatch(logout());
      return;
    }

    dispatch(setRestoringSession(true));
    dispatch(setLoading(true));
    try {
      const result = await authService.restoreEventSession();
      if (!result.ok) {
        dispatch(logout());
        dispatch(setError(result.error));
        return;
      }
      dispatch(setCredentials({ user: result.user }));
      dispatch(setActiveCompany(result.company ?? null));
      dispatch(setCompanies(result.companies ?? []));
    } catch (error) {
      console.error("Failed to restore session:", error);
      dispatch(logout());
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Unable to restore your session. Please sign in again.",
        ),
      );
    } finally {
      dispatch(setRestoringSession(false));
      dispatch(setLoading(false));
    }
  }, [dispatch]);
}
